<template>
  <ApplicationBase
    v-bind="vBindBase"
    color="green"
    applicationType="rent"
    applicationTypeOriginal="rental"
    v-on="vOnBase"
  >

    <template #apartments>
      <ApplicationApartments
        v-bind="vBindApartments"
        color="green"
        v-on="vOnApartments"
        apartmentTypeSearchQueryValue="rental"
      />
    </template>

    <!-- Only render when vuelidate is ready -->
    <template v-if="canRenderForm">
      <div class="container container--narrow">
        <div class="application-section-bg">
          <ApplicationApplicants
            v-bind="vBindApplicants"
            @update="applicants = $event"
            v-slot="{ vApplicant, item, index }"
            applicationType="rent"
          >

            <div class="form__row" v-if="vApplicant && item">
              <InputWrap
                class="form__col-6"
                data-cy="monthly-income"
                :label="translate('Kuukausitulot (brutto)')"
                :validations="vApplicant.GrossIncome"
                v-slot="{error, touch}"
              >
                <InputText
                  v-model="item.GrossIncome"
                  :disabled="preview"
                  :error="error"
                  @blur="touch"
                >
                  €
                </InputText>
              </InputWrap>

              <InputWrap
                v-if="index === 0 && v$ && v$.form"
                class="form__col-6"
                data-cy="financial-status"
                :label="translate('Koko talouden varallisuus')"
                :validations="v$.form.TotalWealth"
                v-slot="{error, touch}"
              >
                <InputText
                  v-model="v$.form.TotalWealth.$model"
                  :disabled="preview"
                  :error="error"
                  @blur="touch"
                >
                  €
                </InputText>
              </InputWrap>
            </div>

            <div class="form__row" v-if="vApplicant && item">
              <InputWrap
                class="form__col-6"
                data-cy="profession"
                :label="translate('Ammatti')"
                :validations="vApplicant.Occupation"
                v-slot="{error, touch}"
              >
                <InputText
                  v-model="item.Occupation"
                  :disabled="preview"
                  :error="error"
                  @blur="touch"
                />
              </InputWrap>

              <InputWrap
                class="form__col-6"
                data-cy="employer"
                :label="translate('Työpaikka')"
                :validations="vApplicant.Employer"
                v-slot="{error, touch}"
              >
                <InputText
                  v-model="item.Employer"
                  :disabled="preview"
                  :error="error"
                  @blur="touch"
                />
              </InputWrap>
            </div>

            <div class="form__row" v-if="vApplicant && item">
              <InputWrapDate
                v-model="item.JobStartDate"
                class="form__col-6"
                data-cy="employment-start-date"
                :label="translate('Työsuhde alkoi')"
                :validations="vApplicant.JobStartDate"
                :disabled="preview"
              />
            </div>

          </ApplicationApplicants>
        </div><!-- application-section-bg -->
      </div><!-- container container--narrow -->

      <ApplicationDivider />

      <ApplicationOtherResidents
        v-if="form.OtherResidents"
        :items="form.OtherResidents || []"
        :item-init="createOtherResident"
        :v-iter="vFormOtherResidentsIter"
        :preview="preview"
        @update="form.OtherResidents = $event"
      />
    </template>
    <div v-else class="container container--narrow">
      <p>{{ translate('Ladataan lomaketta...') }}</p>
    </div>

    <!-- Only render these sections when v$ is ready -->
    <template v-if="v$ && v$.form">
      <div class="container container--narrow">
        <ApplicationSection
          :title="translate('Asunnon tarve')"
          wrapper-classes="application-section-bg mb-ts-2">
          <div class="form">
            <div class="form__grid">
              <div class="form__row">
                <InputWrap
                  class="form__col-6"
                  data-cy="reason-for-apartment"
                  :label="translate('Asunnon tarpeen syy')"
                  :validations="v$.form.ApplicationReason"
                  :wrap-label="false"
                  v-slot="{id, error, touch}"
                >
                  <InputSelect
                    v-model="v$.form.ApplicationReason.$model"
                    :id="id"
                    :error="error"
                    :options="optionsReason"
                    :disabled="preview"
                    simple-value
                    @close="touch"
                  />
                </InputWrap>

                <InputWrapDate
                  v-model="form.NeedForApartmentStartDate"
                  class="form__col-6"
                  data-cy="need-for-apartment-start-date"
                  :label="translate('Asunnon tarve alkaen')"
                  :validations="v$.form.NeedForApartmentStartDate"
                  :disabled="preview"
                />
              </div>

              <div class="form__row">
                <InputWrap
                  class="form__col"
                  data-cy="application-reason-additional-info"
                  :label="translate('Asunnon tarpeen lisätiedot')"
                  :validations="v$.form.ApplicationReasonAdditionalInfo"
                  v-slot="{error, touch}"
                >
                  <InputTextarea
                    v-model="v$.form.ApplicationReasonAdditionalInfo.$model"
                    :preview="preview"
                    :error="error"
                    @blur="touch"
                  />
                </InputWrap>
              </div>
            </div>
          </div>
        </ApplicationSection>
      </div>

      <div class="container container--narrow">
        <ApplicationSection
          :title="translate('Nykyisen asunnon tiedot')"
          wrapper-classes="application-section-bg mb-ts-2"
        >
          <div class="form" id="current-apartment-info">
            <div class="form__grid">
              <div class="form__row">
                <InputWrap
                  class="form__col-6"
                  data-cy="type-of-apartment"
                  :label="translate('Asumismuoto')"
                  :validations="v$.form.CurrentApartmentInfo.AccommodationType"
                  :wrap-label="false"
                  v-slot="{id, error, touch}"
                >
                  <InputSelect
                    v-model="v$.form.CurrentApartmentInfo.AccommodationType.$model"
                    :id="id"
                    :error="error"
                    :options="optionsCurrentAccommodationType"
                    :disabled="preview"
                    simple-value
                    @close="touch"
                  />
                </InputWrap>

                <InputWrap
                  class="form__col-6"
                  data-cy="number-of-rooms"
                  :label="translate('Huoneita')"
                  :validations="v$.form.CurrentApartmentInfo.ApartmentType"
                  :wrap-label="false"
                  v-slot="{id, error, touch}"
                >
                  <InputSelect
                    v-model="v$.form.CurrentApartmentInfo.ApartmentType.$model"
                    :id="id"
                    :error="error"
                    :options="optionsCurrentApartmentType"
                    :disabled="preview"
                    simple-value
                    @close="touch"
                  />
                </InputWrap>
              </div>

              <div class="form__row">
                <InputWrap
                  class="form__col-6"
                  data-cy="area"
                  :label="translate('Pinta-ala')"
                  :validations="v$.form.CurrentApartmentInfo.Area"
                  v-slot="{error, touch}"
                >
                  <InputText
                    v-model="v$.form.CurrentApartmentInfo.Area.$model"
                    :disabled="preview"
                    :error="error"
                    @blur="touch"
                  >
                    m²
                  </InputText>
                </InputWrap>

                <InputWrap
                  class="form__col-6"
                  data-cy="number-of-residents"
                  :label="translate('Asukkaiden lukumäärä')"
                  :validations="v$.form.CurrentApartmentInfo.NumberOfResidents"
                  v-slot="{error, touch}"
                >
                  <InputText
                    v-model="v$.form.CurrentApartmentInfo.NumberOfResidents.$model"
                    :disabled="preview"
                    :error="error"
                    @blur="touch"
                  />
                </InputWrap>
              </div>

              <div class="form__row">
                <InputWrap
                  class="form__col-6"
                  data-cy="rent-amount"
                  :label="translate('Vuokra')"
                  :validations="v$.form.CurrentApartmentInfo.Rent"
                  v-slot="{error, touch}"
                >
                  <InputText
                    v-model="v$.form.CurrentApartmentInfo.Rent.$model"
                    :disabled="preview"
                    :error="error"
                    @blur="touch"
                  >
                    €
                  </InputText>
                </InputWrap>

                <InputWrap
                  class="form__col-6"
                  data-cy="maintenance-charge"
                  :label="translate('Vastike')"
                  :validations="v$.form.CurrentApartmentInfo.MaintenanceCharge"
                  v-slot="{error, touch}"
                >
                  <InputText
                    v-model="v$.form.CurrentApartmentInfo.MaintenanceCharge.$model"
                    :disabled="preview"
                    :error="error"
                    @blur="touch"
                  >
                    €
                  </InputText>
                </InputWrap>
              </div>
            </div>
          </div>
        </ApplicationSection>
      </div>
    </template>

    <input type="hidden" name="Checksum" :value=form.Checksum />

  </ApplicationBase>
</template>

<script>
  import {
    required,
    maxLength,
    integer,
    minValue,
    maxValue,
    helpers,
  } from '@vuelidate/validators';

  import {
    validations,
    extendApplicantCreator,
  } from '../lib/applicationShared';
  import application from './mixins/application';
  import globals from '../lib/globals';

  const mapApplicantExtend = (applicant) => ({
    GrossIncome: applicant.GrossIncome,
    Occupation: applicant.Occupation,
    Employer: applicant.Employer,
    JobStartDate: applicant.JobStartDate,
  });

  export default {
    mixins: [
      application({
        favouritesType: 'rental',
        presetMapExtend: {
          mapApplicant: mapApplicantExtend,
        },
        localSaveExtend: {
          localSaveFromStateForm(state) {
            return {
              TotalWealth: state.form.TotalWealth,
              ApplicationReason: state.form.ApplicationReason,
              NeedForApartmentStartDate: state.form.NeedForApartmentStartDate,
              ApplicationReasonAdditionalInfo: state.form.ApplicationReasonAdditionalInfo,
              CurrentApartmentInfo: state.form.CurrentApartmentInfo,
            };
          },
          localSaveFromStateApplicant: mapApplicantExtend,
          stateFromLocalSave(state, localSave) {
            state.form.TotalWealth = localSave.form.TotalWealth;
            state.form.ApplicationReason = localSave.form.ApplicationReason;
            state.form.NeedForApartmentStartDate = localSave.form.NeedForApartmentStartDate;
            state.form.ApplicationReasonAdditionalInfo = localSave.form.ApplicationReasonAdditionalInfo;
            state.form.CurrentApartmentInfo = localSave.form.CurrentApartmentInfo;
          },
        },
        applyOtherData: {
          conditionFavouritesType: 'right-of-occupancy',
          title: 'Korissasi on myös ASO-asuntoja',
          ctaText: 'Jätä ASO-asuntohakemus',
          ctaUrl: globals.urlApplicationRightOfOccupancy,
          color: 'pink',
        },
      }),
    ],
    data() {
      const createOptions = (labels, translate) => labels.map((label, index) => ({
        label: translate ? this.translate(label) : label,
        value: (index + 1).toString(),
      }));
      return {
        optionsReason: createOptions([
          'Asunnoton',
          'Muuttovelvoite nykyisestä asunnosta',
          'Liian pieni asunto',
          'Vuokran suuruus',
          'Paikkakunnan vaihto',
          'Muu syy',
          'Terveydelliset syyt',
          'Liian suuri asunto',
          'Avio- / avoliitto',
          'Avio- / avoero',
          'Asunnon huono kunto',
          'Epätyydyttävä asunto',
          'Itsenäistyminen',
        ], true),
        optionsCurrentAccommodationType: createOptions([
          'Asumisoikeusasunto',
          'Vuokra-asunto',
          'Omistusasunto',
          'Osaomistusasunto',
          'Alivuokra-asunto',
          'Asun vanhempien luona',
          'Työsuhdeasunto',
          'Opiskelija-asunto',
        ], true),
        optionsCurrentApartmentType: createOptions([
          1,
          2,
          3,
          4,
          '5+',
        ]),
        // Component data() replaces the mixin `form` object entirely (Vue shallow merge);
        // include the same base keys as mixins/application.js so OtherResidents and ARA fields exist.
        form: {
          Id: null,
          OtherResidents: [],
          AdditionalWishes: '',
          ASOAraOrderNumber: '',
          isARA: false,
          ARACurrentAccommodationType: '',
          ARACurrentMunicipality: '',
          Checksum: '',
          Type: '2',
          TotalWealth: '',
          ApplicationReason: null,
          NeedForApartmentStartDate: null,
          ApplicationReasonAdditionalInfo: '',
          CurrentApartmentInfo: {
            AccommodationType: null,
            ApartmentType: null,
            Area: '',
            NumberOfResidents: '',
            Rent: '',
            MaintenanceCharge: '',
          },
        },
      };
    },
    validations() {
      return {
        applicants: {
          $each: helpers.forEach({
            ...validations.applicant,
            ...{
              GrossIncome: {
                required,
                integer,
                minValue: minValue(0),
                maxValue: maxValue(100000),
              },
              Occupation: {
                required,
                maxLength: maxLength(255),
              },
              Employer: {
                required,
                maxLength: maxLength(100),
              },
              JobStartDate: {},
            },
          }),
        },
        form: {
          ...validations.form,
          TotalWealth: {
            required,
            integer,
            minValue: minValue(0),
            maxValue: maxValue(50000000),
          },
          ApplicationReason: {
            required,
          },
          NeedForApartmentStartDate: {
            required,
          },
          ApplicationReasonAdditionalInfo: {
            maxLength: maxLength(2000),
          },
          CurrentApartmentInfo: {
            ApartmentType: {
              required,
            },
            AccommodationType: {
              required,
            },
            Area: {
              integer,
            },
            NumberOfResidents: {
              integer,
            },
            Rent: {
              integer,
              minValue: minValue(0),
              maxValue: maxValue(10000),
            },
            MaintenanceCharge: {
              integer,
              minValue: minValue(0),
              maxValue: maxValue(10000),
            },
          },
        },
      };
    },
    methods: {
      createApplicant: extendApplicantCreator({
        GrossIncome: '',
        Occupation: '',
        Employer: '',
        JobStartDate: null,
      }),
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";
</style>
